import { QueryClient } from '@tanstack/react-query';

import { ClientDataReconciliationQueryKeys } from '@/modules/onboarding/clients-setup/types/clients.types';
import { ProjectDataReconciliationQueryKeys } from '@/modules/onboarding/projects-setup/types';
import { TeamDataReconciliationQueryKeys } from '@/modules/onboarding/team-setup/types/employees.types';

/** Stale time for onboarding queries — prevents excessive refetching on focus/mount */
export const ONBOARDING_QUERY_STALE_TIME = 1 * 60 * 1000;

/**
 * Shared utility functions for invalidating onboarding query caches.
 * These ensure consistent cache invalidation across the onboarding flow
 * without excessive backend load.
 *
 * Dependency matrix:
 * - Projects depend on Clients (projects have a client field)
 * - Team members are independent
 * - Clients are independent
 *
 * When an entity changes, invalidate its own queries + dependent entities.
 */

/**
 * Invalidates all onboarding project queries (list + validation report).
 */
export const invalidateOnboardingProjectQueries = async (
  queryClient: QueryClient
): Promise<void> => {
  await queryClient.invalidateQueries({
    queryKey: [ProjectDataReconciliationQueryKeys.Projects],
  });
  await queryClient.invalidateQueries({
    queryKey: [ProjectDataReconciliationQueryKeys.ValidationReport],
  });
};

/**
 * Invalidates only the onboarding project validation report.
 * Use when the entity list is refreshed through a custom refill strategy.
 */
export const invalidateOnboardingProjectValidationReport = async (
  queryClient: QueryClient
): Promise<void> => {
  await queryClient.invalidateQueries({
    queryKey: [ProjectDataReconciliationQueryKeys.ValidationReport],
  });
};

/**
 * Invalidates all onboarding team member queries (list + validation report).
 */
export const invalidateOnboardingTeamMemberQueries = async (
  queryClient: QueryClient
): Promise<void> => {
  await queryClient.invalidateQueries({
    queryKey: [TeamDataReconciliationQueryKeys.TeamMembers],
  });
  await queryClient.invalidateQueries({
    queryKey: [TeamDataReconciliationQueryKeys.ValidationReport],
  });
};

/**
 * Invalidates only the onboarding team member validation report.
 * Use when the entity list is refreshed through a custom refill strategy.
 */
export const invalidateOnboardingTeamMemberValidationReport = async (
  queryClient: QueryClient
): Promise<void> => {
  await queryClient.invalidateQueries({
    queryKey: [TeamDataReconciliationQueryKeys.ValidationReport],
  });
};

/**
 * Invalidates all onboarding client queries (list + validation report).
 */
export const invalidateOnboardingClientQueries = async (
  queryClient: QueryClient
): Promise<void> => {
  await queryClient.invalidateQueries({
    queryKey: [ClientDataReconciliationQueryKeys.Clients],
  });
  await queryClient.invalidateQueries({
    queryKey: [ClientDataReconciliationQueryKeys.ValidationReport],
  });
};

/**
 * Removes all onboarding client list cache entries (does NOT remove the validation report).
 * Use when clients may have been added by an external operation (e.g. project import)
 * while the user is on a different step. Removing the cache guarantees a fresh fetch
 * on the next mount — invalidation alone only marks entries stale, which may not
 * trigger a visible refresh if TanStack Query serves the stale cache before the
 * background refetch completes.
 */
export const removeOnboardingClientListCache = (queryClient: QueryClient): void => {
  queryClient.removeQueries({
    queryKey: [ClientDataReconciliationQueryKeys.Clients],
  });
};

/**
 * Invalidates only the onboarding client validation report.
 * Use when the entity list is refreshed through a custom refill strategy.
 */
export const invalidateOnboardingClientValidationReport = async (
  queryClient: QueryClient
): Promise<void> => {
  await queryClient.invalidateQueries({
    queryKey: [ClientDataReconciliationQueryKeys.ValidationReport],
  });
};

/**
 * Invalidates client queries and dependent project queries.
 * Use after client operations (create, update, delete, merge, import)
 * since projects reference clients.
 */
export const invalidateOnboardingClientRelatedQueries = async (
  queryClient: QueryClient
): Promise<void> => {
  await invalidateOnboardingClientQueries(queryClient);
  // Projects depend on clients (project has a client field)
  await invalidateOnboardingProjectQueries(queryClient);
};
